import { doesConcretePathMatchPattern } from '@terra/shared-asset-processing';
import {
  countParamPlaceholders,
  extractPathnameWithHashRoute,
} from '@terra/shared-utils';
import { isAiAssetTags } from '@terra/shared-schemas-orchestrator';
import type { AssetPreviewDto } from '@terra/shared-contracts-assets';
import { ASSET_VERIFIED, type AssetVerified } from '@terra/ui-design-system';
import {
  ASSET_TYPE,
  type AssetType,
} from '../../store/tab/assetsTabScopedFilterStore';

export const ASSET_MERGE_NEW_SURVIVOR_VALUE = 'new';

export type AssetMergeEndpoint = Extract<
  AssetPreviewDto,
  { assetType: 'endpoint' }
>;
export type AssetMergeWebpage = Extract<
  AssetPreviewDto,
  { assetType: 'webpage' }
>;
export type AssetMergeCandidate = AssetMergeEndpoint | AssetMergeWebpage;

export type AssetMergeBlockReason =
  | 'tooFew'
  | 'mixedType'
  | 'mixedDomain'
  | 'mixedSegmentCount'
  | 'mixedMethod';

export type AssetMergeChoice =
  | { kind: 'EXISTING'; id: string }
  | { kind: 'NEW'; path: string };

export function isAssetMergeChromeVisible({
  isEnabled,
  typeFilter,
  verified,
}: {
  isEnabled: boolean;
  typeFilter: AssetType;
  verified: AssetVerified;
}): boolean {
  return (
    isEnabled &&
    typeFilter !== ASSET_TYPE.AGENTS &&
    verified !== ASSET_VERIFIED.NO
  );
}

export function isAssetMergeCandidate(
  asset: AssetPreviewDto,
): asset is AssetMergeCandidate {
  if (asset.assetType === 'unverifiedEndpoint') {
    return false;
  }

  if (
    asset.assetType === 'endpoint' &&
    isAiAssetTags(asset.endpointAssetTags)
  ) {
    return false;
  }

  return asset.assetType === 'endpoint' || asset.assetType === 'webpage';
}

export function getWebpageOrigin(url: string): string | undefined {
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
}

export function getWebpagePathname(url: string): string {
  try {
    const pathname = extractPathnameWithHashRoute(url);

    return pathname.length > 0 ? pathname : '/';
  } catch {
    const [path] = url.split('?');

    return path?.startsWith('/') ? path : `/${path ?? ''}`;
  }
}

export function getAssetMergePath(asset: AssetMergeCandidate): string {
  return asset.assetType === 'endpoint'
    ? asset.path
    : getWebpagePathname(asset.url);
}

export function getAssetMergeDomainKey(
  asset: AssetMergeCandidate,
): string | undefined {
  if (asset.assetType === 'endpoint') {
    return asset.host.toLowerCase();
  }

  return getWebpageOrigin(asset.url)?.toLowerCase();
}

export function getAssetMergeSegmentCount(asset: AssetMergeCandidate): number {
  return getAssetMergePath(asset).split('/').filter(Boolean).length;
}

export function getAssetMergeMethod(
  asset: AssetMergeCandidate,
): string | undefined {
  return asset.assetType === 'endpoint' ? asset.request.method : undefined;
}

export function getAssetMergeParamCount(asset: AssetMergeCandidate): number {
  return countParamPlaceholders(getAssetMergePath(asset));
}

export function getAssetMergeBlockReason(
  assets: readonly AssetMergeCandidate[],
): AssetMergeBlockReason | undefined {
  if (assets.length < 1) {
    return 'tooFew';
  }

  if (new Set(assets.map((asset) => asset.assetType)).size > 1) {
    return 'mixedType';
  }

  const domains = new Set(assets.map(getAssetMergeDomainKey));

  if (domains.size > 1 || domains.has(undefined)) {
    return 'mixedDomain';
  }

  if (new Set(assets.map(getAssetMergeSegmentCount)).size > 1) {
    return 'mixedSegmentCount';
  }

  if (
    assets[0]?.assetType === 'endpoint' &&
    new Set(assets.map(getAssetMergeMethod)).size > 1
  ) {
    return 'mixedMethod';
  }

  return undefined;
}

export function isAssetMergeReviseSelection(
  assets: readonly AssetMergeCandidate[],
): boolean {
  return assets.length === 1;
}

export function isAssetMergeReviseCommand({
  survivorKind,
  loserCount,
}: {
  survivorKind: string | undefined;
  loserCount: number;
}): boolean {
  return survivorKind === 'NEW' && loserCount === 1;
}

export function assetMergeToastTitleKey({
  outcome,
  isRevise,
}: {
  outcome: 'started' | 'done' | 'failed' | 'error';
  isRevise: boolean;
}):
  | 'started'
  | 'done'
  | 'failed'
  | 'error'
  | 'reviseStarted'
  | 'reviseDone'
  | 'reviseFailed'
  | 'reviseError' {
  if (!isRevise) {
    return outcome;
  }

  if (outcome === 'started') {
    return 'reviseStarted';
  }

  if (outcome === 'done') {
    return 'reviseDone';
  }

  if (outcome === 'failed') {
    return 'reviseFailed';
  }

  return 'reviseError';
}

export function isAssetMergeConfirmReady({
  choice,
  newPath,
  assets,
}: {
  choice: { kind: 'EXISTING'; id: string } | { kind: 'NEW' } | undefined;
  newPath: string;
  assets: readonly AssetMergeCandidate[];
}): boolean {
  if (choice?.kind === 'EXISTING') {
    return choice.id.length > 0;
  }

  if (choice?.kind !== 'NEW') {
    return false;
  }

  const trimmedPath = newPath.trim();

  if (!trimmedPath.startsWith('/')) {
    return false;
  }

  if (!newPathAllowsHash({ path: trimmedPath, assets })) {
    return false;
  }

  if (!isAssetMergeReviseSelection(assets)) {
    return true;
  }

  const original = assets[0];

  if (!original) {
    return false;
  }

  const originalPath = getAssetMergePath(original);

  if (trimmedPath === originalPath) {
    return false;
  }

  if (countParamPlaceholders(trimmedPath) < 1) {
    return true;
  }

  if (
    trimmedPath.split('/').filter(Boolean).length !==
    getAssetMergeSegmentCount(original)
  ) {
    return false;
  }

  return doesConcretePathMatchPattern(originalPath, trimmedPath);
}

function newPathAllowsHash({
  path,
  assets,
}: {
  path: string;
  assets: readonly AssetMergeCandidate[];
}): boolean {
  if (!path.includes('#')) {
    return true;
  }

  return assets[0]?.assetType === 'webpage';
}

export function composeWebpageMergeUrl({
  origin,
  path,
}: {
  origin: string;
  path: string;
}): string {
  return `${origin}${path}`;
}
